import Head from 'next/head';
import { AppProps } from 'next/app';
import { SnackbarProvider } from 'notistack';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';

import { AuthProvider } from '@/contexts/AuthContext';
import { theme } from '@/styles/theme';
import createEmotionCache from '@/config/createEmotionCache';
import { queryClient } from '@/services/queryClient';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AppVersionUpdateProvider } from '@/contexts/AppVersionUpdateContext';
import { AppUpdateModal } from '@/components/common/AppVersionUpdateModal';
import { GuestRoute } from '@/components/common/GuestRoute';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { NextPageWithRouteAccess } from '@/modules/core/auth/types/route-access';

import '../styles/globals.css';

const clientSideEmotionCache = createEmotionCache();

interface MyAppProps extends AppProps {
  Component: NextPageWithRouteAccess;
  emotionCache?: EmotionCache;
}
function MyApp({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache,
}: MyAppProps) {
  const page = <Component {...pageProps} />;
  const { routeAccess } = Component;
  let guardedPage = page;

  if (routeAccess?.type === 'protected') {
    guardedPage = (
      <ProtectedRoute
        permissions={routeAccess.permissions}
        strategy={routeAccess.strategy}
      >
        {page}
      </ProtectedRoute>
    );
  } else if (routeAccess?.type === 'guest') {
    guardedPage = <GuestRoute>{page}</GuestRoute>;
  }

  return (
    <>
      <Head>
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta name="google" content="notranslate" key="notranslate" />
        <link rel="icon" href="/favicon.ico" />
        <title>Água Amigão | Web</title>
      </Head>
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <CssBaseline />
            <SnackbarProvider maxSnack={3}>
              <AuthProvider>
                <AppVersionUpdateProvider>
                  <AppUpdateModal />
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    {guardedPage}
                  </LocalizationProvider>
                </AppVersionUpdateProvider>
              </AuthProvider>
            </SnackbarProvider>
            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          </QueryClientProvider>
        </ThemeProvider>
      </CacheProvider>
      <Analytics />
    </>
  );
}

export default MyApp;
